import React from 'react';
import Article, { ArticleHeader, ArticleContent, Markdown, ArticleTableOfContents, ArticleFooter, ArticleDisplayAd, ArticleSectionHeader } from '@/components/Article';
import { Layout, MainLayout, SectionLayout, SidebarLayout, TableOfContentsLayout } from '@/common/components/layouts/Article';
import useActiveSection from '@/common/components/Article/useActiveSection';
import settings from './settings';
import { titleize } from '@/common/utils/strings';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SECTIONS = settings.articleSections || [];

const PageArticle = _ref => {
  let {
    authors,
    reviewers,
    updatedAt,
    createdAt,
    children
  } = _ref;
  const currentYear = new Date().getFullYear();
  const {
    activeSection,
    onSectionSelect,
    onSectionVisibility
  } = // eslint-disable-next-line react-hooks/rules-of-hooks
  useActiveSection({
    sections: SECTIONS
  });
  return /*#__PURE__*/_jsxs(Layout, {
    hasTableOfContents: true,
    children: [/*#__PURE__*/_jsx(TableOfContentsLayout, {
      hasTopPadding: true,
      children: /*#__PURE__*/_jsx(ArticleTableOfContents, {
        items: SECTIONS,
        activeItemId: activeSection ? activeSection.id : '',
        onActivate: onSectionSelect
      })
    }), /*#__PURE__*/_jsx(MainLayout, {
      children: /*#__PURE__*/_jsxs(SectionLayout, {
        children: [/*#__PURE__*/_jsx(ArticleHeader, {
          heading: `Setting Your ${currentYear} Wedding Budget`,
          subHeading: 'With an Easy-to-Use Wedding Calculator',
          reviewer: reviewers === null || reviewers === void 0 ? void 0 : reviewers[0],
          breadcrumbs: [{
            href: `/${settings.directory}`,
            text: titleize(settings.directory)
          }, {
            text: 'Weddings'
          }],
          authors: authors || [],
          updatedAt: updatedAt,
          createdAt: createdAt
        }), /*#__PURE__*/_jsxs(Article, {
          children: [/*#__PURE__*/_jsx(ArticleContent, {
            children: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "It\u2019s time to plan a 2023 wedding, but you need help setting a realistic budget. As you plan your costs, you\u2019ll need expert advice, a good wedding calculator, and an understanding of what other couples are spending."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Wondering what a realistic wedding budget is? Couples will likely spend an **average of $31,770 for their weddings in 2023.** This is based on the [2021](https://www.sounddollar.com/how-much-does-a-wedding-cost) [average](https://www.sounddollar.com/how-much-does-a-wedding-cost) and then adjusted for inflation."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Ahead, we\u2019ll answer your biggest budget questions and share simple ways to cut costs. Plus, we have a wedding cost calculator that will help you stay flexible yet on track throughout your engagement."
                })]
              }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[0].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[0].name
              }), /*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "So now that we know that 2023 wedding budgets will land upward of $31,000, what does that mean for your big day and your wedding calculator?"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Many things influence a wedding budget, from where you live, your personal style, and even your past relationships. Keep reading to learn more about these wedding expenses."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "**Size:** The average guest count in 2021 was 105 people, so keep that in mind as you look at your wedding cost estimator.\\*\\* The guest count will influence catering costs, location options, and overall vibe."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "**Location:** Wedding budgets vary greatly by location. For example, a New York wedding averaged $42,000 in 2021, while Oklahoma saw an average cost of $16,000."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "If you\u2019re on a budget, you may want to see if it\u2019s worth having an out-of-state wedding to trim expenses for you and your guests."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "**Style:** While there are always ways to splurge and save, you should know that the vibe of your big day will influence your wedding calculator costs. Consider creating a mood board with budget-friendly inspiration before you become devoted to a high-cost wedding vision."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "**First vs. Second Weddings:** The average budget is cut in half when it\u2019s the [second wedding for both partners](https://www.fool.com/the-ascent/research/average-cost-wedding/). Of course, it doesn\u2019t have to be that way for you, but it\u2019s still helpful to understand spending patterns as you look toward your own wedding budget calculator."
                })]
              }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[1].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[1].name
              }), /*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "So where is that $30,000 going? A lot goes into weddings, but the **[top three wedding expenses](https://www.theknot.com/content/wedding-budget-ways-to-save-money) are the venue, catering, and the band**. In fact, these three components make up about 55% of the total wedding budget. So if you want to cut costs, look at these expenses first."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### #1 Venue - The Greatest Expense for a Wedding Calculator"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "On average, a venue rental comprises 26% of a wedding budget planner. So if you\u2019re planning a $30,000 wedding, a venue rental could run about $7,700."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Common venues include hotels, farms, renovated warehouses, restaurants, botanical gardens, and golf clubs. If you want to cut your budget, consider these alternative locations:"
                }), /*#__PURE__*/_jsxs(_Fragment, {
                  children: ["- **State or National Parks** - Many parks have scenic locations for small ceremonies and lodges for bigger receptions.", '', "- **Backyards -** Keep in mind that rentals quickly add up, so look at the overall costs on a wedding calculator before choosing this seemingly free option.", '', "- **Community Centers** - They typically include seating and full catering kitchens at a super low cost, but they lack the sophistication of dedicated venues.", '', "- **Summer Camps and Nonprofit Centers** - Many organizations have a location that can accommodate groups. This makes camps, gardens, and nonprofit centers a hidden gem for budget-minded weddings.", '']
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### #2 Catering"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Catering typically accounts for 19% of wedding budgets. If you want to lower your food and beverage costs, look for a venue that allows outside catering and alcohol. This way, you can customize the menu, price, and service style to meet your unique vision."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Caterers can offer table service, buffets, or simple drop-off trays."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### #3 Band"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Rounding out \u201CThe Big Three\u201D of wedding budgeting is the band. This is also an expense that\u2019s easy to forego."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "While you can\u2019t beat the thrill of live music, opting for a DJ will save a lot of money. In 2021, couples spent an average of $4,300 on a live band, while DJs ran about $1,400."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Simple weddings could lower their budget even more by doing a DIY playlist with a Bluetooth Speaker."
                })]
              }), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[2].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[2].name
              }), /*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "So how much do you want to spend on _your_ wedding? If you\u2019re still deciding what your ideal wedding will cost, we can help. Below we\u2019ve used the national averages for wedding expenses, our easy wedding calculator, and our planning expertise to outline how much you could spend in 2023."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### $50,000"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "A couple budgeting $50,000 for their 2023 wedding could splurge for a luxe $12,000 venue, a $5,000 band, and a $90 per-plate meal for 105 guests."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### $30,000"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "We now know that ~$30,000 is the average cost for a 2023 wedding. If this is your budget, you\u2019ll likely spend $7,700 on a venue and $5,600 on catering."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### $20,000"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Some states view $20,000 as an above-average budget, while some areas cost over twice as much. For this amount, look for a $5,000 location and $3,800 catering. Cut your guest count to find a lower-cost venue and make your food budget go even further."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### $15,000"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "$15,000 is putting us closer to the low-cost wedding range, so you\u2019ll have to find ways to save. Though you may end up spending nearly $4,000 on a venue, finding somewhere cheaper could allow you to include more elaborate decor and catering."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### $10,000"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "A $10,000 wedding is certainly possible in many markets, but you will have to reign in your vision. A $2,500 venue will keep your wedding budget spreadsheet in check, and a 50-person guest count will make a $3,800 catering allotment more reasonable."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### $5,000"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "You\u2019ve decided on a $5,000 budget, and you\u2019re sticking to it! You can still have a memorable celebration for this price, but you\u2019ll have to eliminate a lot of typical wedding elements."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Opt for a DIY playlist instead of a DJ. A student-level photographer might capture the ceremony and some portraits for $300. Order trays of BBQ if you need to feed a lot of guests for $950, or take 10-20 people to a restaurant."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "##### $1,000"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Some couples spend $1,000 or less on their wedding, even if that seems impossible. A state park or community center could run for $250 or less. Instead of offering a meal, serve trays of finger food, punch, and dessert."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "And of course, you can always elope and treat yourselves to a nice dinner and some portraits for under $1,000."
                })]
              }), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[3].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[3].name
              }), /*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "While it\u2019s always helpful to know what other people are spending on weddings, at the end of the day, you should budget based on your own individual circumstance."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "With compromise, thoughtfulness, and creativity (and a good wedding calculator!), you can have a wedding that is true to who you are as a couple AND true to your budget."
                })]
              }), children]
            })
          }), /*#__PURE__*/_jsx(ArticleFooter, {
            authors: authors,
            citations: [{
              url: 'https://www.sounddollar.com/how-much-does-a-wedding-cost',
              text: 'How Much Does a Wedding Cost?'
            }, {
              url: 'https://www.bls.gov/data/inflation_calculator.htm',
              text: 'CPI Inflation Calculator'
            }, {
              url: 'https://www.theknot.com/content/wedding-budget-ways-to-save-money',
              text: 'What Your Wedding Budget Should Look Like'
            }, {
              url: 'https://www.theknot.com/content/average-cost-wedding-alcohol',
              text: "Here's How Much the Average Couple Spends on Alcohol for Their Wedding"
            }, {
              url: 'https://www.minted.com/wedding-ideas/wedding-budgets',
              text: 'How to Budget For a Wedding: Breakdowns, Examples, and Steps to Making a Good Wedding Budget'
            }, {
              url: 'https://www.fool.com/the-ascent/research/average-cost-wedding/',
              text: 'The Average Cost of a Wedding in 2020'
            }]
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(SidebarLayout, {
      isMobileCollapsed: true,
      hasTopPadding: true,
      children: /*#__PURE__*/_jsx(ArticleDisplayAd, {
        isSidebar: true
      })
    })]
  });
};

export default PageArticle;